import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { UncontrolledTooltip } from 'reactstrap'
import { ReactComponent as InOffice } from '../../../assets/images/icons/in-office.svg'
import {
    fmtActivityTimestamp,
    fmtCreatedAt,
    fmtDate,
    fmtFullDateTime,
    fmtMoney,
    toGlobalId,
} from './helpers'
import Assignees from './Assignees'
import CaseDetailPanel from './CaseDetailPanel'
import RowActions from './RowActions'

function prettyType(t) {
    if (!t) return ''
    return t
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function CaseRow({
    collectionCase: c,
    columnCount,
    rowIndex,
    isActiveSidebarDriver,
    onSelectDriverForSidebar,
}) {
    const [expanded, setExpanded] = useState(false)
    const [focusActivity, setFocusActivity] = useState(false)
    // Balance shown in the table = opening balance minus what's been collected.
    const outstanding = Number(c.driverChargeableBalance)
    const driverGid = toGlobalId('DriverNode', c.driverId)
    const isClosed = c.status === 'CLOSED'

    // Show a pill under the driver name when their active car is in a
    // disabled/repo state. Maps the raw tracker status to a compact label.
    const carStatus = c.driverActiveCarId ? c.driverActiveCarStatus : null
    const carStatusPill =
        carStatus === 'Repo'
            ? { label: 'REPO', cls: 'car-repo' }
            : carStatus === 'Disabled'
            ? { label: 'CAR DISABLED', cls: 'car-disabled' }
            : carStatus === 'Pending Disable'
            ? { label: 'PENDING DISABLE', cls: 'car-pending-disable' }
            : null

    const statusPillClass = isClosed
        ? 'cd-pill status-closed'
        : c.chargeAttempts > 0
        ? 'cd-pill status-in-progress'
        : 'cd-pill status-open'
    const statusLabel = isClosed
        ? 'RESOLVED'
        : c.chargeAttempts > 0
        ? 'IN PROGRESS'
        : 'OPEN'

    const openForEdit = () => {
        setExpanded(true)
        setFocusActivity(true)
    }

    const selectDriverForSidebar = () => {
        if (!driverGid || !onSelectDriverForSidebar) return
        onSelectDriverForSidebar({
            id: driverGid,
            rawId: Number(c.driverId),
        })
    }

    return (
        <>
            <tr
                className={`cd-row${expanded ? ' expanded' : ''}${isClosed ? ' closed-tint' : ''}${isActiveSidebarDriver ? ' active-driver' : ''}`}
                onClick={() => {
                    selectDriverForSidebar()
                    setExpanded((x) => !x)
                    setFocusActivity(false)
                }}
            >
                <td className="row-index">{rowIndex}</td>
                <td>
                    <div className="cd-driver">
                        <div>
                            <div className="cd-driver-name">
                                <Link
                                    to={`/driver/${driverGid}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {c.driverName || '—'}
                                </Link>
                            </div>
                            <div className="cd-driver-meta">
                                <span>#{c.driverId}</span>
                                {c.driverPhone && <span>· {c.driverPhone}</span>}
                                {c.driverInOffice && (
                                    <>
                                        <InOffice
                                            id={`cdInOffice${c.id}`}
                                            width={14}
                                            height={14}
                                            fill="#198754"
                                        />
                                        <UncontrolledTooltip target={`cdInOffice${c.id}`}>
                                            Driver in Office
                                        </UncontrolledTooltip>
                                    </>
                                )}
                                {c.driverHasMobileApp === false && (
                                    <>
                                        <i
                                            id={`cdNoMobile${c.id}`}
                                            className="fa fa-mobile text-danger"
                                        />
                                        <UncontrolledTooltip
                                            placement="bottom"
                                            target={`cdNoMobile${c.id}`}
                                        >
                                            Not Using Mobile App
                                        </UncontrolledTooltip>
                                    </>
                                )}
                            </div>
                            {carStatusPill && (
                                <div className="cd-driver-carstatus">
                                    <span className={`cd-pill ${carStatusPill.cls}`}>
                                        <i className="fa fa-car" />
                                        {carStatusPill.label}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </td>
                <td>
                    {c.driverBranchName && (
                        <span className="cd-pill branch">{c.driverBranchName}</span>
                    )}
                </td>
                <td>
                    <Assignees caseId={c.id} assignedTo={c.assignedTo ?? []} />
                </td>
                <td>
                    <span className={statusPillClass}>{statusLabel}</span>
                </td>
                <td title={fmtFullDateTime(c.openedAt)}>
                    <div className="cd-cell-text">
                        {fmtCreatedAt(c.openedAt)}
                    </div>
                </td>
                <td>
                    <div className="cd-cell-text">
                        {fmtMoney(c.openingBalance)}
                    </div>
                </td>
                <td>
                    <div
                        className={`cd-balance ${
                            outstanding > 0
                                ? 'negative'
                                : outstanding < 0
                                ? 'positive'
                                : ''
                        }`}
                        title={`Opening ${fmtMoney(c.openingBalance)} − Collected ${fmtMoney(c.amountCollected)}`}
                    >
                        {fmtMoney(outstanding)}
                    </div>
                    <div className="cd-balance-sub">
                        Collected {fmtMoney(c.amountCollected)}
                    </div>
                </td>
                <td>
                    <div className="cd-cell-text-soft">
                        {c.chargeAttempts} / {c.maxChargeAttempts}
                    </div>
                </td>
                <td
                    title="When the next automated charge attempt is scheduled to fire (per backoff). Null once attempts are exhausted or the case closes."
                >
                    <div className="cd-cell-text">
                        {fmtDate(c.nextChargeAttemptAt)}
                    </div>
                </td>
                <td>
                    <div className="cd-latest-activity">
                        <div className="body">
                            {c.latestActivityType ? (
                                <>
                                    <div className="label">
                                        {prettyType(c.latestActivityType)}
                                    </div>
                                    {c.latestActivityNote && (
                                        <div className="note">{c.latestActivityNote}</div>
                                    )}
                                    {c.latestActivityAt && (
                                        <div className="ts">
                                            {fmtActivityTimestamp(c.latestActivityAt)}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="empty">No activity yet</div>
                            )}
                        </div>
                    </div>
                </td>
                <td className="cd-rowactions-cell">
                    <RowActions
                        collectionCase={c}
                        onOpenPencil={openForEdit}
                        expanded={expanded}
                        isActiveSidebarDriver={isActiveSidebarDriver}
                        onSelectDriverForSidebar={selectDriverForSidebar}
                        onToggleExpand={() => {
                            selectDriverForSidebar()
                            setExpanded((x) => !x)
                            setFocusActivity(false)
                        }}
                    />
                </td>
            </tr>
            {expanded && (
                <tr>
                    <td colSpan={columnCount} style={{ padding: 0 }}>
                        <CaseDetailPanel
                            collectionCase={c}
                            focusActivity={focusActivity}
                            onActivityFocused={() => setFocusActivity(false)}
                        />
                    </td>
                </tr>
            )}
        </>
    )
}
